'use client';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ButtonBase, SxProps, Theme, Typography } from '@mui/material';
import type { CSSObject } from '@mui/material/styles';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, useId } from 'react';
import { identityCardWidth } from '../shared/identityCardWidth';
import { identityOptionSurface } from '../shared/identityOptionSurface';
import { SelectedPill } from '../shared/SelectedPill';
import { NEW_IDENTITY_CARD_COPY } from './newIdentityCardCopy';

export type NewIdentityCardProps = {
  /** The entity type's name, such as the type tile's title. Used in the default label. */
  entityTypeLabel: string;
  /** Whether verifying a new one is the chosen option. The parent owns the choice. */
  selected: boolean;
  /** Reports a tap. It takes no id: the parent knows which entity type this is. */
  onSelect: () => void;
  /** `card` sits in the identity row. `row` is the phone option row under the row. */
  variant: 'card' | 'row';
  /** Replaces the default label. */
  label?: string;
  /** Text of the pill on a chosen option. */
  selectedLabel?: string;
  /** Lets a parent radio group manage the tab stop. Defaults to the browser's own. */
  tabIndex?: 0 | -1;
  'data-testid'?: string;
};

type VariantParts = {
  LeadingIcon: (selected: boolean) => typeof AddIcon;
  defaultLabel: (entityTypeLabel: string) => string;
  surfaceSx: (theme: Theme) => CSSObject;
  iconFontSize: (theme: Theme) => string;
  labelVariant: 'body2' | 'subtitle1';
  labelSx: SxProps<Theme>;
};

const CARD_LABEL_SX: SxProps<Theme> = {
  maxWidth: '100%',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
};

const ROW_LABEL_SX: SxProps<Theme> = (theme: Theme) => ({
  fontSize: theme.semantic.fontSize.base,
  textAlign: 'center',
});

// What differs between the card in the identity row and the phone option row.
const VARIANTS: Record<NewIdentityCardProps['variant'], VariantParts> = {
  card: {
    LeadingIcon: () => AddIcon,
    defaultLabel: NEW_IDENTITY_CARD_COPY.cardLabel,
    surfaceSx: (theme) => ({
      flex: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.semantic.spacing.xs,
      width: identityCardWidth(theme),
      height: theme.identityCard.height,
      padding: theme.semantic.spacing.sm,
      textAlign: 'center',
    }),
    iconFontSize: (theme) => theme.semantic.fontSize.xl,
    labelVariant: 'body2',
    labelSx: CARD_LABEL_SX,
  },
  row: {
    // A chosen row swaps its plus for a tick, as the ruled board draws it.
    LeadingIcon: (selected) => (selected ? CheckCircleIcon : AddIcon),
    defaultLabel: NEW_IDENTITY_CARD_COPY.rowLabel,
    surfaceSx: (theme) => ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.semantic.spacing.sm,
      width: '100%',
      maxWidth: theme.identityCard.optionRowMaxWidth,
      // Centres a row that the cap holds narrower than its container.
      marginInline: 'auto',
      paddingBlock: theme.semantic.spacing.sm,
      paddingInline: theme.semantic.spacing.base,
    }),
    iconFontSize: (theme) => theme.semantic.fontSize.l,
    labelVariant: 'subtitle1',
    labelSx: ROW_LABEL_SX,
  },
};

/**
 * The option to verify a new entity of one type, drawn like an identity card (`card`) or as a full
 * width option row on a phone (`row`). A radio, chosen when `selected`.
 */
function NewIdentityCardComponent({
  entityTypeLabel,
  selected,
  onSelect,
  variant,
  label,
  selectedLabel = NEW_IDENTITY_CARD_COPY.selected,
  tabIndex,
  'data-testid': dataTestId,
}: Readonly<NewIdentityCardProps>): ReactElement {
  const labelId = useId();
  const parts = VARIANTS[variant];
  const LeadingIcon = parts.LeadingIcon(selected);

  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      disableRipple
      aria-labelledby={labelId}
      tabIndex={tabIndex}
      onClick={() => onSelect()}
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        ...identityOptionSurface(theme, { selected, available: true }),
        ...parts.surfaceSx(theme),
      })}
    >
      <LeadingIcon
        aria-hidden="true"
        sx={(theme: Theme) => ({ flex: 'none', fontSize: parts.iconFontSize(theme) })}
      />
      <Typography id={labelId} variant={parts.labelVariant} component="span" sx={parts.labelSx}>
        {label ?? parts.defaultLabel(entityTypeLabel)}
      </Typography>
      {selected && <SelectedPill label={selectedLabel} />}
    </ButtonBase>
  );
}

export const NewIdentityCard = withClarityTheme(NewIdentityCardComponent);
